import { getMessageResp } from "#src/types/messages.ts";
import type { GetEventListResp, GetMessageListResp } from "#src/types/threadEvents.ts";
import { threadConfigSchema } from "#src/types/threadConfig.ts";
import { arrayQueryParam } from "#src/types/utils.ts";
import { z } from "zod";

export const threadStatus = z.enum(["completed", "in-progress", "to-review", "draft", "dead"]);

// Default status filter for threads
export const defaultStatusFilter = "in-progress,to-review";

export const threadActivityState = z.enum([
  "running",
  "waitingForUser",
  "waitingForExternalEvent",
  "stopPending",
  "stopped",
]);

export const threadChannel = z.enum([
  "app",
  "text_message",
  "slack",
  "teams",
  "mcp",
  "whatsapp",
  "email",
]);
export type ThreadChannel = z.infer<typeof threadChannel>;

/**
 * Derived card status — the single view-model value the client uses to decide
 * badge, body copy, and CTAs on the home card. Computed server-side from
 * `thread.status` + `thread.activityState` and returned as `card.status`.
 */
export const cardStatus = z.enum([
  "waiting", // waiting for an external event (participant, system)
  "paused", // thread.isPaused === true
  "waitingForUser", // thread.activityState === "waitingForUser"
  "inProgress",
  "completed",
]);
export type CardStatus = z.infer<typeof cardStatus>;

/**
 * Participant shown in the card's avatar facepile. Minimal shape —
 * downstream Avatar component only needs a name + optional picture/contact
 * for tooltips + sidebar linkage.
 */
export const cardParticipant = z.object({
  handleId: z.string(),
  contactId: z.string().optional(),
  name: z.string(),
  picture: z.string().optional(),
  value: z.string().optional(),
});
export type CardParticipant = z.infer<typeof cardParticipant>;

export const cardViewModel = z.object({
  status: cardStatus,
  /**
   * Single-line summary describing the thread's current state in enough
   * detail for the user to decide whether to fire the primary action — e.g.
   * "Email to Alex: Lunch next week — Hi Alex, want to grab lunch?".
   * Per-artifact overrides land in A-series sub-issues under BIG-4119. The
   * client clamps to 2 lines.
   */
  body: z.string(),
  /**
   * Transient labels computed server-side on read — separate from the
   * persisted `thread.labels` array. Use these for ephemeral status chips
   * like `invitation-conflict` where the value depends on external state
   * (e.g. the user's calendar at fetch time) and shouldn't be written to
   * the DB. Clients render chips from `thread.card.labels` alongside the
   * persisted labels; order doesn't matter.
   */
  labels: z.array(z.string()).optional(),
  /**
   * Up to 2 most-recently-engaged-with participants across the thread's
   * active artifact. Client renders as an avatar facepile + `+N` overflow
   * badge. The current user is never included.
   */
  participants: z.array(cardParticipant).optional(),
  /** Count of additional participants beyond the first 2 shown as avatars. */
  additionalParticipantCount: z.number().optional(),
});
export type CardViewModel = z.infer<typeof cardViewModel>;

/** All available thread channels */
export const allChannels: ThreadChannel[] = [
  "app",
  "text_message",
  "slack",
  "teams",
  "mcp",
  "whatsapp",
  "email",
];

/** External channels (non-app). */
export const externalChannels: ThreadChannel[] = [
  "text_message",
  "slack",
  "teams",
  "mcp",
  "whatsapp",
  "email",
];

/**
 * Human messaging channels — the subset of {@link externalChannels} where a real
 * person is on the other end (SMS/iMessage, Slack, Teams, WhatsApp). Excludes
 * `mcp`, which is a programmatic tool-call channel (another agent driving okthx),
 * not a conversation to surface in the inbox. Mirrors `messagingChannels` in
 * `schemas/threads.ts`; kept in sync by `threadChannel.test.ts`.
 */
export const messagingChannels: ThreadChannel[] = ["text_message", "slack", "teams", "whatsapp"];

/**
 * Default channel filter for threads. An `email` thread is the user's own email
 * conversation with okthx, whose replies go to the user alone, so it lists with
 * the app threads — including a brief the user answered by email (BIG-9012).
 */
export const defaultChannels: ThreadChannel[] = ["app", "email"];

export const actionPermissionsSchema = z.object({
  autoSendCommunication: z.boolean(),
});

export type ThreadActivityState = z.infer<typeof threadActivityState>;

/** Query params for GET /threads list endpoint */
export const getThreadsQuerySchema = z.object({
  /** Statuses to filter by */
  status: arrayQueryParam(threadStatus, "status", {
    // Deployed native clients may still send the legacy `paused` status while
    // OTA updates propagate. Tolerate it rather than returning 400; pause is
    // represented by the orthogonal `is_paused` flag.
    ignore: (value) => value === "paused",
  }),
  /** Activity states to filter by */
  activityState: arrayQueryParam(threadActivityState, "activityState"),
  /** Labels — threads matching any of these are returned. Free-form, so any value parses. */
  labels: arrayQueryParam(z.string(), "labels"),
  /** Page size limit */
  limit: z.coerce.number().int().min(1).max(100).default(10),
  /** Page number (0-indexed) */
  page: z.coerce.number().int().min(0).default(0),
  /** Sort order by sortDate */
  order: z.enum(["asc", "desc"]).default("desc"),
  /** Channels to filter by. Unscoped means the in-app inbox, not every channel. */
  channel: arrayQueryParam(threadChannel, "channel", { whenBlank: defaultChannels }),
  /**
   * `pinned` serves the page as `GetThreadsGroupedResp` — the filter's pins and
   * the date-ordered remainder as two arrays — instead of one flat array with
   * the pins hoisted to the front. Opt-in because deployed native binaries
   * still read the flat shape.
   */
  groupBy: z.enum(["pinned"]).optional(),
  /**
   * Hoist the threads wearing the brand dot above the rest, within the pinned
   * group and the unpinned remainder alike. Opt-in: a client that omits it gets
   * `sort_date` order.
   *
   * `stringbool` rather than `coerce.boolean` because this arrives as a query
   * string, and `z.coerce.boolean()` reads "false" as `true` — a client that
   * spelled the default out would silently turn the sort on.
   *
   * `catch` rather than `default` so a blank `?needsAttentionFirst=` reads as
   * off instead of 400-ing the whole list. The rest of this schema already
   * treats blank and absent alike (see `arrayQueryParam`), and failing the
   * inbox over a sort flag is the wrong trade.
   */
  needsAttentionFirst: z.stringbool().catch(false),
});

export type GetThreadsQuery = z.infer<typeof getThreadsQuerySchema>;

export const getThreadResp = z.object({
  id: z.number(),
  status: threadStatus,
  /** Orthogonal pause kill-switch (BIG-5169); independent of `status`. */
  isPaused: z.boolean(),
  /** When the user pinned this thread, or null if they haven't. Pinned threads
   * sort above every unpinned one; among themselves, newest pin first. */
  pinnedAt: z.coerce.date().nullable(),
  title: z.string(),
  sortDate: z.coerce.date(),
  lastVisitedAt: z.string().nullable(),
  labels: z.array(z.string()).optional().nullable(),
  unreadCount: z.number(),
  isSeen: z.boolean(),
  activeMessage: getMessageResp.optional().nullable(),
  activityState: threadActivityState,
  channel: threadChannel,
  /**
   * The routine bound to this thread, if any. Either a user routine's ent id
   * or the `system:<slug>` sentinel for system routines. Drives the
   * "Edit routine" action in the thread header. Nullable: most threads aren't
   * routine-based.
   */
  routineEntId: z.string().nullable().optional(),
  /**
   * Per-thread settings bag (`threads.config`). The omnibox model-switcher
   * reads it directly so it doesn't need a separate fetch. Nullable because
   * the DB column is nullable (no override set yet).
   */
  config: threadConfigSchema.nullable().optional(),
  /**
   * Server-owned card view-model. Populated by thread list / thread detail
   * handlers when they resolve the caller's timezone; other fetch paths may
   * omit it, so clients MUST treat this as optional and fall back to their
   * local derivation.
   */
  card: cardViewModel.optional(),
});

export type GetThreadResp = z.infer<typeof getThreadResp>;
export type ThreadStatus = z.infer<typeof threadStatus>;

/**
 * A thread-list page served with `groupBy=pinned`. `pinned` holds every thread
 * matching the filter that carries a `pinnedAt`, most recently pinned first,
 * and only on page 0 — later pages return it empty. `rest` is the date-ordered
 * remainder, cut by `limit` and `page` alone, so the client pages on its length.
 */
export const getThreadsGroupedResp = z.object({
  pinned: z.array(getThreadResp),
  rest: z.array(getThreadResp),
});

export type GetThreadsGroupedResp = z.infer<typeof getThreadsGroupedResp>;

/** The thread fields `card` is derived from; `deriveCardStatus` takes this. */
export type ThreadCardSource = Pick<GetThreadResp, "status" | "isPaused" | "activityState">;

/**
 * Fields that decide a thread's placement in a list: every query filter that is
 * also a thread field, plus the sort keys. Derived from the two schemas so a new
 * filter is picked up on its own — the alternative is a hand-kept list that
 * goes quietly stale and strands threads in the wrong tab. `limit`, `page` and
 * `order` drop out because a response never carries them.
 *
 * `sortDate` and `pinnedAt` are named explicitly because they order the list
 * without filtering it, so neither appears in the query schema to be derived
 * from.
 */
const THREAD_LIST_PLACEMENT_FIELDS = [
  "sortDate",
  "pinnedAt",
  // Orders without filtering, like the two above: with `needsAttentionFirst`
  // it decides which bucket a thread sits in, so pausing one has to move it.
  "isPaused",
  ...Object.keys(getThreadsQuerySchema.shape).filter((field) => field in getThreadResp.shape),
];

/** The fields {@link ThreadCardSource} is built from. */
const THREAD_CARD_SOURCE_FIELDS = [
  "status",
  "isPaused",
  "activityState",
] as const satisfies readonly (keyof ThreadCardSource)[];

/** Every persisted thread field used by `getAccountThreadBadgeCounts`. */
const THREAD_BADGE_FIELDS = new Set<string>([
  "status",
  "isPaused",
  "activityState",
  "channel",
  "labels",
  "sortDate",
] as const satisfies readonly (keyof GetThreadResp)[]);

/**
 * Whether a `thread:mutated` diff can have moved a thread within a list or
 * changed which lists it belongs to, meaning the client must let the server
 * re-sort and re-filter rather than patch the row in place. Sound only because
 * the server emits just the columns a write touched (`buildThreadDiff`).
 */
export const affectsThreadListPlacement = (diff: Partial<GetThreadResp>): boolean =>
  THREAD_LIST_PLACEMENT_FIELDS.some((field) => field in diff);

/**
 * Whether a `thread:mutated` diff invalidates the server-derived `card`. `card`
 * is built per request rather than stored, so it never rides a diff and a
 * merged patch cannot bring it up to date — only a refetch can.
 */
export const affectsThreadCard = (diff: Partial<GetThreadResp>): boolean =>
  THREAD_CARD_SOURCE_FIELDS.some((field) => field in diff);

/** Whether a persisted thread diff can change either awareness-badge tally. */
export const affectsThreadBadge = (diff: Partial<GetThreadResp>): boolean =>
  Object.keys(diff).some((field) => THREAD_BADGE_FIELDS.has(field));

export const createNewTopicThreadReq = z.object({
  title: z.string().optional(),
  artifactIds: z.array(z.string()).optional(),
  action: z
    .enum([
      "autoLearnMore",
      "autoReply",
      "autoReplyAll",
      "autoForward",
      "autoMemoryConfirmed",
      "autoSendEmailDraft",
      "autoCancelEmailDraft",
      "autoSendInvitation",
      "autoCancelInvitation",
      "autoAcceptRsvp",
      "autoDeclineRsvp",
      "autoTentativeRsvp",
      "autoMakePhoneCall",
      "autoCancelPhoneCall",
      "autoAskUserFormSubmit",
      "newThread",
    ])
    .optional(),
  /**
   * Optional per-thread settings to seed the new thread with. Used by the
   * omnibox model-switcher so a user can pick a provider / agent version
   * before sending the first message of a new conversation.
   */
  config: threadConfigSchema.optional(),
});
export type CreateNewTopicThreadReq = z.infer<typeof createNewTopicThreadReq>;

export type UpdateThreadVisitedAtResp = Pick<GetThreadResp, "lastVisitedAt" | "isSeen">;

export const createNewTopicThreadResp = z.object({
  id: z.number(),
});

export const updateThreadReq = z.object({
  /**
   * User-edited thread title. Empty string clears the title (server will
   * regenerate it on the next auto-title-gen pass). Trimmed server-side;
   * capped to something reasonable so free text doesn't blow the home card
   * clamp.
   */
  title: z.string().max(200).optional(),
});

export type UpdateThreadReq = z.infer<typeof updateThreadReq>;

// TODO: clean up all of this "topic" thread/message naming
export type CreateNewTopicThreadResp = z.infer<typeof createNewTopicThreadResp>;

/** One of a thread's tasks, as the debug viewer shows it. */
export type ThreadDebugTask = {
  id: string;
  title: string;
  status: string;
  /** "user" | "okthx" */
  assignee: string;
  /**
   * The sources that created it: an email, a meeting, a document. A thread
   * keeps no roles, so a thread with any evidence on the task is here too:
   * one that found it, updated it or closed it. A thread that only works on
   * the task is `threadId`, not a source.
   */
  producerUrns: string[];
  /** The thread okthx works on it in, when there is one. */
  threadId: number | null;
  createdAtMs: number;
  closedAtMs: number | null;
  closedReason: string | null;
  /** A suggestion the user dismissed, which otherwise reads as cancelled. */
  dismissed: boolean;
};

/**
 * The thread's tasks as they were when the debug link was generated. The event
 * timeline is what the actor saw and did at each step; this is the rows those
 * steps left, at capture time.
 */
export type ThreadTaskSnapshot = {
  capturedAtMs: number;
  tasks: ThreadDebugTask[];
};

export type ThreadDebugData =
  | {
      error: false;
      accountId: number;
      threadId: number;
      threadData: GetThreadResp;
      messageList: GetMessageListResp;
      // Fresh fetches always populate eventList, but historical debug-data JSON
      // pasted into the ThreadDebug viewer may pre-date it.
      eventList?: GetEventListResp;
      // Same for the tasks: links made before they were captured have none.
      taskSnapshot?: ThreadTaskSnapshot;
    }
  | {
      error: true;
      accountId: number;
      threadId: number;
      threadData: null;
      messageList: null;
      eventList: null;
      errorMessage?: string;
    };

export type ThreadsDebugData = ThreadDebugData[];

/**
 * Shown to the admin when a debug blob is gone. Debug ids carry no back-reference
 * to their thread, so recovering the trace means finding the thread id first — the
 * copy has to say that rather than imply a one-click retry. Lives here so the API
 * response and the admin console render the same words without drifting.
 */
export const DEBUG_DATA_EXPIRED_MESSAGE =
  "This debug link has expired. Debug traces are deleted 90 days after they are generated. To inspect this thread again, look up its thread ID and generate a fresh debug link from the admin thread tools.";

export type CreateThreadDebugDataResp = {
  id: string;
  url: string;
  // do NOT include the full data - we're attaching debug fields to it, which we don't want accessible to all users
};
